import { useCallback, useEffect, useRef, useState } from 'react';
import { useSimStore } from '../store/simStore';
import { CreatureState } from '../models/Creature';
import { WorldState } from '../models/World';

export const SimulationCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { world, isPaused } = useSimStore();
  const [worldState, setWorldState] = useState<WorldState>(world.getState());

  const drawCreature = useCallback((ctx: CanvasRenderingContext2D, creature: CreatureState) => {
    const size = 20 * creature.traits.bodySize;
    
    ctx.save();
    ctx.translate(creature.position.x, creature.position.y);

    // Draw body
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.lineWidth = 2;
    ctx.fillStyle = creature.traits.color;
    ctx.beginPath();

    switch (creature.traits.bodyShape) {
      case 'triangle':
        ctx.moveTo(-size/2, size/2);
        ctx.lineTo(size/2, size/2);
        ctx.lineTo(0, -size/2);
        ctx.closePath();
        break;
      case 'square':
        ctx.rect(-size/2, -size/2, size, size);
        break;
      case 'star':
        for (let i = 0; i < 10; i++) {
          const angle = (i * Math.PI) / 5;
          const radius = i % 2 === 0 ? size/2 : size/4;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
        break;
      default:
        ctx.arc(0, 0, size/2, 0, Math.PI * 2);
    }

    ctx.fill();
    ctx.stroke();

    // Draw appendages
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 2;
    creature.traits.appendages.forEach((_, i) => {
      const angle = (i * Math.PI * 2) / creature.traits.appendages.length;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(
        Math.cos(angle) * size,
        Math.sin(angle) * size
      );
      ctx.stroke();
    });

    // Draw energy bar
    const energyWidth = size * 1.5;
    const energyHeight = 4;
    ctx.fillStyle = '#FF0000';
    ctx.fillRect(-energyWidth/2, size + 2, energyWidth, energyHeight);
    ctx.fillStyle = '#00FF00';
    ctx.fillRect(
      -energyWidth/2,
      size + 2,
      energyWidth * (creature.energy / 100),
      energyHeight
    );

    ctx.restore();
  }, []);

  const drawFood = useCallback((ctx: CanvasRenderingContext2D, food: WorldState['food'][0]) => {
    ctx.save();
    ctx.translate(food.position.x, food.position.y);

    // Draw food glow
    ctx.fillStyle = food.color.replace(')', ', 0.3)').replace('rgb', 'rgba');
    ctx.beginPath();
    ctx.arc(0, 0, 8, 0, Math.PI * 2);
    ctx.fill();

    // Draw food body
    ctx.fillStyle = food.color;
    ctx.beginPath();
    ctx.arc(0, 0, 4, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let lastTime = performance.now();
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!isPaused) {
        const deltaTime = (currentTime - lastTime) / 1000;
        world.update(deltaTime);
        setWorldState(world.getState());
      }
      lastTime = currentTime;

      // Clear canvas
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw food
      worldState.food.forEach(food => drawFood(ctx, food));

      // Draw creatures
      worldState.creatures.forEach(creature => drawCreature(ctx, creature));

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [world, isPaused, worldState, drawCreature, drawFood]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      }}
    />
  );
}; 